import { FormikProps, Field } from 'formik';
import * as React from 'react';
import { Element } from 'react-scroll';
import * as Yup from 'yup';
import {
  SubForm,
  FormSection,
  FormCheckBox,
  Heading,
  Box,
  Icon,
} from '@alinta-digital/asg';
import * as icons from '@alinta-digital/asg/icons/';
import { FuelSite } from '../../types';
import { FormContainerContext } from '../../contexts/FormContainerContext';
import {
  setFormContextError,
  scrollToScrollElement,
  resetFormContextError,
  pushAnalytics,
} from '../../utils/utils';
import { ErrorNotification } from '../ErrorNotification';
import { DirectDebitForm } from '../../views/Form/Form';
import { useMutation } from '@apollo/client';
import {
  CONFIRM_CARD_WEBSITE,
  REGISTER_DIRECT_DEBIT,
  REGISTER_DIRECT_DEBIT_STRIPE,
} from '../../utils/Account.gql';
import { useStripe } from '@stripe/react-stripe-js';
import { ButtonV2, StyledLink, StyledText } from '../../Common.Styled';
import { analyticsLoaderStep4 } from '../../utils/analytics.helper';
import { useFeatureFlag } from '../../utils/useFeatureFlag';

interface ReviewSubForm {
  consent: boolean;
}

export interface ReviewParentForm {
  review: ReviewSubForm;
}

interface ReviewSubFormProps {
  formikProps: FormikProps<DirectDebitForm>;
  isEdit: boolean;
  onPreviousClick: () => void;
  onNextClick: () => void;
}

export const ReviewSubFormCore: React.FC<ReviewSubFormProps> = ({
  formikProps,
  onPreviousClick,
  onNextClick,
}) => {
  const {
    connection,
    debitDetails,
    review,
    identifyYourAccountForm,
  } = formikProps.values;
  const { electricity: parentFormElectricity, gas: parentFormGas } = connection;

  const {
    values: formContextValues,
    dispatch: formContextDispatch,
  } = React.useContext(FormContainerContext);

  const accountType =
    parentFormGas?.[0]?.accountType || parentFormElectricity?.[0]?.accountType;

  const tncResidentialUrl = 'https://www.alintaenergy.com.au/directdebit-tcs';
  const tncBusinessUrl =
    'https://www.alintaenergy.com.au/nsw/-/jssmedia/alinta-website/documents/sme/22-024-direct-debit-form-sme-online.pdf';
  const tncDownloadUrl =
    accountType?.toLocaleLowerCase() === 'business'
      ? tncBusinessUrl
      : accountType?.toLocaleLowerCase() === 'residential'
      ? tncResidentialUrl
      : '';

  const { error: formContextError } = formContextValues;

  const stripe = useStripe();
  const [submitLoading, setSubmitLoading] = React.useState(false);
  const { data: featureData } = useFeatureFlag('MyA_IsStripeEnabled');
  const isStripeEnabled = featureData?.featureValue;

  const [registerDirectDebit] = useMutation(REGISTER_DIRECT_DEBIT, {
    onCompleted: (data) => {
      if (data.registerDirectDebitWebsite?.successfullySubmitted) {
        onNextClick();
        return true;
      } else {
        setFormContextError(
          formContextValues,
          formContextDispatch,
          3,
          true,
          'Submission error'
        );
      }
    },
    onError: () => {
      setFormContextError(
        formContextValues,
        formContextDispatch,
        3,
        true,
        'Submission error'
      );
    },
  });
  const [registerDirectDebitStripe] = useMutation(
    REGISTER_DIRECT_DEBIT_STRIPE,
    { fetchPolicy: 'no-cache' }
  );
  const [confirmCardWebsite] = useMutation(CONFIRM_CARD_WEBSITE, {
    onCompleted: (data) => {
      if (data.confirmCardWebsite.isSuccess) {
        onNextClick();
      } else {
        setFormContextError(
          formContextValues,
          formContextDispatch,
          3,
          true,
          'Submission error'
        );
      }
    },
    onError: () => {
      setFormContextError(
        formContextValues,
        formContextDispatch,
        3,
        true,
        'Submission error'
      );
    },
    fetchPolicy: 'no-cache',
  });
  React.useEffect(() => {
    if (formContextError.steps[3].status) {
      scrollToScrollElement('notificationError');
    } else {
      scrollToScrollElement('reviewStart');
    }
  });

  React.useEffect(() => {
    pushAnalytics('load.0.dd.s4');
    analyticsLoaderStep4();
  }, []);
  return (
    <SubForm<ReviewParentForm, ReviewSubForm>
      formikProps={formikProps}
      config={{
        review: {
          consent: {
            validation: Yup.string()
              .required('Please read and agree')
              .test(
                'validation',
                'Please read and agree',
                (userInput: string) => {
                  if (userInput === 'true') {
                    return true;
                  }
                  return false;
                }
              ),
            initialValue: review.consent,
          },
        },
      }}
      valuesKey="review"
    >
      {({ validateSubForm, subFormFormikProps }) => {
        const nominatedMethod =
          debitDetails.paymentMethod === 'bankTransfer'
            ? 'nominated account'
            : 'nominated card';
        return (
          <FormSection
            id="Review"
            heading="Step 4: Review and confirm"
            stepNumber="4"
            totalSteps="4"
            previousButton={
              !formContextError.steps[3].status ? (
                <ButtonV2
                  id="reviewPreviousButton"
                  data-testid="review_previousButton"
                  disabled={submitLoading}
                  secondary={true}
                  type="button"
                  onClick={() => {
                    pushAnalytics('click.3.dd.s4.back');
                    resetFormContextError(
                      formContextValues,
                      formContextDispatch,
                      3
                    );
                    onPreviousClick();
                  }}
                >
                  Back
                </ButtonV2>
              ) : (
                undefined
              )
            }
            nextButton={
              !formContextError.steps[3].status ? (
                <ButtonV2
                  id="reviewNextButton"
                  data-testid="review_nextButton"
                  disabled={!subFormFormikProps.values.review.consent}
                  loading={submitLoading}
                  primary={true}
                  type="button"
                  onClick={async () => {
                    pushAnalytics('click.4.dd.s4.sudd');
                    setSubmitLoading(true);
                    const { isValid } = await validateSubForm();

                    if (!isValid) {
                      setSubmitLoading(false);
                      return false;
                    }

                    formikProps.setValues({
                      ...formikProps.values,
                      ...subFormFormikProps.values,
                    });

                    const cardExpiryDay = debitDetails.creditCard
                      ? new Date(
                          parseInt(debitDetails.creditCard?.expiryYear),
                          parseInt(debitDetails.creditCard?.expiryMonth),
                          0
                        ).getDate()
                      : null;

                    if (
                      isStripeEnabled &&
                      debitDetails.paymentMethod !== 'bankTransfer'
                    ) {
                      const stripeResult = await registerDirectDebitStripe({
                        variables: {
                          input: {
                            accountNumber: [
                              identifyYourAccountForm.accountNumber,
                            ],
                            paymentMethodId: debitDetails.creditCard?.token,
                            source: 'website',
                            requestType: 'register',
                          },
                        },
                      });

                      const clientSecret =
                        stripeResult?.data?.directDebit?.setupDirectDebitWeb
                          ?.clientSecret;

                      if (clientSecret && stripe) {
                        const {
                          setupIntent,
                          error: stripeError,
                        } = await stripe.confirmCardSetup(clientSecret, {
                          payment_method: debitDetails.creditCard?.token,
                        });

                        if (stripeError) {
                          setFormContextError(
                            formContextValues,
                            formContextDispatch,
                            3,
                            true,
                            'Submission error'
                          );
                          setSubmitLoading(false);
                          return;
                        }

                        const setupIntentId = setupIntent.id;
                        await confirmCardWebsite({
                          variables: {
                            input: {
                              paymentMethodId: debitDetails.creditCard?.token,
                              setupIntentId,
                              accountNumber: [
                                identifyYourAccountForm.accountNumber,
                              ],
                              source: 'WEBSITE',
                              requestType: 'REGISTER',
                              customerId:
                                formContextValues.customer.customerNumber,
                            },
                          },
                        });
                      } else {
                        // No 3DS required
                        if (
                          stripeResult?.data?.directDebit?.setupDirectDebitWeb
                            ?.successfullySubmitted
                        ) {
                          onNextClick();
                        } else {
                          setFormContextError(
                            formContextValues,
                            formContextDispatch,
                            3,
                            true,
                            'Submission error'
                          );
                        }
                      }
                    } else {
                      await registerDirectDebit({
                        variables: {
                          registerDirectDebitInput: {
                            customerNumber:
                              formContextValues.customer.customerNumber,
                            accountNumbers: [
                              ...parentFormElectricity,
                              ...parentFormGas,
                            ]
                              .map((fuel: FuelSite) => fuel.accountNumber)
                              .filter(
                                (accountNumber: string) => !!accountNumber
                              ),
                            source: 'WEBSITE',
                            entityContactType: 'CONTACTPRIMARY',
                            requestType: 'REGISTER',
                            ddType:
                              debitDetails.paymentMethod === 'bankTransfer'
                                ? 'Bank'
                                : 'CC',
                            ccDetails:
                              debitDetails.paymentMethod === 'bankTransfer'
                                ? null
                                : {
                                    token: debitDetails.creditCard?.token,
                                    type: debitDetails.cardType?.toUpperCase(),
                                    expiry: `${debitDetails.creditCard?.expiryYear}-${debitDetails.creditCard?.expiryMonth}-${cardExpiryDay}`,
                                    number: `************${debitDetails.creditCard?.last4}`,
                                    name: debitDetails.cardName,
                                  },
                            bankDetails:
                              debitDetails.paymentMethod !== 'bankTransfer'
                                ? null
                                : {
                                    bank:
                                      formContextValues.bankBranch.displayName,
                                    bankBranch:
                                      formContextValues.bankBranch.displayName,
                                    bankBSB: debitDetails.bsbNumber,
                                    bankAccountNumber:
                                      debitDetails.bankAccountNumber,
                                  },
                          },
                        },
                      });
                    }
                  }}
                >
                  Setup Direct Debit
                </ButtonV2>
              ) : (
                undefined
              )
            }
          >
            {formContextError.steps[3].status ? (
              <ErrorNotification
                errorType={formContextError.steps[3].type}
                stateShortName={formContextValues.stateShortName}
                onClick={() => {
                  setFormContextError(
                    formContextValues,
                    formContextDispatch,
                    3,
                    false,
                    undefined
                  );
                  setSubmitLoading(false);
                }}
              />
            ) : (
              <>
                <Element name="reviewStart" id="reviewStart" />
                <Box margin={{ bottom: '16px' }}>
                  <Heading level={3} color="#000F20" weight={700}>
                    {parentFormElectricity.length && parentFormGas.length
                      ? 'Electricity & gas'
                      : parentFormElectricity.length
                      ? 'Electricity'
                      : 'Gas'}
                  </Heading>
                </Box>
                <Box margin={{ bottom: 'small' }}>
                  <StyledText lineHeight="21px" data-testid="review_text">
                    You&apos;re almost there. Just confirming that you&apos;d
                    like us to setup direct debit for your Alinta Energy{' '}
                    {parentFormElectricity.length
                      ? ` Electricity Account
                  ${parentFormElectricity.map(
                    (site: FuelSite) => `${site.authAccountNumber}`
                  )}`
                      : parentFormGas.length
                      ? ` Gas Account ${parentFormGas.map(
                          (site: FuelSite) => `${site.authAccountNumber}`
                        )}`
                      : ''}
                    . To continue, click on the Setup Direct Debit Button.
                  </StyledText>
                </Box>
                <Box margin={{ bottom: '14px' }}>
                  <Field
                    component={FormCheckBox}
                    name="review.consent"
                    id="review.consent"
                    data-testid="consent_checkBox"
                    label={
                      <StyledText>
                        I authorise Alinta Energy Retail Sales Pty Ltd ABN 22
                        149 658 300 (User ID No. 398218), until further notice,
                        to debit my {nominatedMethod} through the Direct Debit
                        system for amounts due, in accordance with the{' '}
                        <StyledLink
                          icon={
                            <Icon
                              id="externalLinkIcon"
                              icon={icons.ExternalLink}
                              css={{
                                height: '16px !important',
                                width: '16px !important',
                                marginBottom: '-1px !important',
                              }}
                            />
                          }
                          download
                          iconAlign="right"
                          href={tncDownloadUrl}
                          onClick={() => pushAnalytics('click.2.dd.s4.ddal')}
                        >
                          Direct Debit Service Agreement
                        </StyledLink>
                        I understand that the amount debited may vary and will
                        include any applicable discounts.
                      </StyledText>
                    }
                  />
                </Box>
              </>
            )}
          </FormSection>
        );
      }}
    </SubForm>
  );
};
